import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import UserService from '../../services/user/user.service'; 
import { Toast } from 'primereact/toast'; 

interface Role {
    roleId: number;
    name: string;
    isAssigned: boolean;
}

interface Props {
    assignedRoles: Role[];
    unassignedRoles: Role[];
    userId: number | null;
    onRefresh: () => void;
}

const RoleAccessDatatable: React.FC<Props> = ({ assignedRoles, unassignedRoles, userId,onRefresh }) => {
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(false);
    const toastRef = React.useRef<Toast>(null);

    useEffect(() => {
        const mergedRoles = [
            ...assignedRoles.map(role => ({ ...role, isAssigned: true })),
            ...unassignedRoles.map(role => ({ ...role, isAssigned: false }))
        ];
        setRoles(mergedRoles);
    }, [assignedRoles, unassignedRoles]);

    const onRoleToggle = (roleId: number) => {
        setRoles(prev =>
            prev.map(role =>
                role.roleId === roleId ? { ...role, isAssigned: !role.isAssigned } : role
            )
        );
    };

    const renderCheckbox = (rowData: Role) => {
        return (
            <Checkbox
                checked={rowData.isAssigned}
                onChange={() => onRoleToggle(rowData.roleId)}
                disabled={!userId}
            />
        );
    };

    const handleSave = async () => {
        if (!userId) return;

        setLoading(true);
        
        try {
            const changes = roles.filter(role => {
                const originalAssigned = assignedRoles.some(r => r.roleId === role.roleId);
                return role.isAssigned !== originalAssigned;
            });

            const updatePromises = changes.map(role => 
                UserService.assignUserRole({
                    userId: userId,
                    roleId: role.roleId,
                    isActive: role.isAssigned
                })
            );

            const results = await Promise.all(updatePromises);
            
            if (results.every(r => r.isSuccess)) {
                toastRef.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Role assignments updated successfully',
                    life: 3000
                });
                    onRefresh(); 
                
            }
            
            else {
                throw new Error('Some updates failed');
            }
        } catch (error) {
            console.error('Error saving roles:', error);
            toastRef.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update role assignments',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mt-4">
            <Toast ref={toastRef} />  
            <DataTable value={roles} className="p-datatable-sm">
                <Column field="name" header="Role Name" />
                <Column 
                    body={renderCheckbox} 
                    header="Assigned" 
                    style={{ width: '120px' }} 
                />
            </DataTable>
            <Button
                label="Save Changes"
                icon="pi pi-save"
                className="mt-3"
                onClick={handleSave}
                disabled={!userId || loading}
                loading={loading}
            />
        </div>
    );
};

export default RoleAccessDatatable;